import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { formatPHP, formatErrorDetail } from "@/lib/api";
import { BriefcaseBusiness, MapPin, Building2, CheckCircle2, Upload, Loader2, Share2 } from "lucide-react";
import { copyLink } from "./PublicCareers";

const field = "w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400";

export default function PublicApply() {
    const { token } = useParams();
    const [params] = useSearchParams();
    const ref = params.get("ref") || "";

    const [job, setJob] = useState(null);
    const [loadErr, setLoadErr] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState("");
    const [resumeName, setResumeName] = useState("");
    const [form, setForm] = useState({
        first_name: "", last_name: "", email: "", phone: "",
        location: "", linkedin: "", current_title: "", current_company: "", expected_salary: "",
    });
    const [resume, setResume] = useState(null);

    useEffect(() => {
        api.get(`/public/jobs/${token}`)
            .then((r) => setJob(r.data))
            .catch((e) => setLoadErr(formatErrorDetail(e.response?.data?.detail) || "This job posting is not available."));
    }, [token]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!form.first_name || !form.last_name || !form.email) {
            setErr("Please fill in your name and email.");
            return;
        }
        setSubmitting(true);
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
        if (ref) fd.append("ref", ref);
        if (resume) fd.append("resume", resume);
        try {
            await api.post(`/public/jobs/${token}/apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setDone(true);
        } catch (e2) {
            setErr(formatErrorDetail(e2.response?.data?.detail) || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loadErr) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
                <div className="max-w-md text-center">
                    <BriefcaseBusiness className="size-10 mx-auto text-slate-300 mb-3" />
                    <h1 className="text-lg font-semibold text-slate-800">Posting unavailable</h1>
                    <p className="text-sm text-slate-500 mt-1">{loadErr}</p>
                </div>
            </div>
        );
    }

    if (!job) {
        return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm"><Loader2 className="size-4 animate-spin mr-2" /> Loading…</div>;
    }

    if (done) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
                <div className="max-w-md text-center bg-white rounded-2xl border shadow-sm p-8">
                    <CheckCircle2 className="size-12 mx-auto text-emerald-500 mb-3" />
                    <h1 className="text-xl font-bold text-slate-800">Application received</h1>
                    <p className="text-sm text-slate-500 mt-2">
                        Thanks for applying to <span className="font-medium">{job.title}</span>
                        {job.company_name ? ` at ${job.company_name}` : ""}. Our hiring team will review your application and reach out if there’s a match.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
                {/* Job header */}
                <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
                    {job.has_photo && (
                        <img
                            src={`${api.defaults.baseURL || "/api"}/public/jobs/${token}/photo`}
                            alt={job.title}
                            className="w-full max-h-64 object-cover"
                            data-testid="job-photo-banner"
                        />
                    )}
                    <div className="p-6 md:p-8">
                    <div className="flex items-center justify-between gap-2 text-xs text-slate-500 mb-2">
                        <span>{job.company_name && <span className="inline-flex items-center gap-1"><Building2 className="size-3.5" /> {job.company_name}</span>}</span>
                        <button
                            type="button"
                            data-testid="job-share-btn"
                            onClick={async () => {
                                const url = window.location.origin + `/careers/${token}`;
                                const title = `${job.title}${job.company_name ? ` at ${job.company_name}` : ""}`;
                                if (navigator.share) { try { await navigator.share({ title, text: `We're hiring: ${title}`, url }); return; } catch { /* fall back */ } }
                                if (await copyLink(url)) alert("Job link copied — ready to share");
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-700"
                        >
                            <Share2 className="size-3.5" /> Share
                        </button>
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900">{job.title}</h1>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-600">
                        {job.department && <span className="inline-flex items-center gap-1"><BriefcaseBusiness className="size-4" /> {job.department}</span>}
                        {job.location && <span className="inline-flex items-center gap-1"><MapPin className="size-4" /> {job.location}</span>}
                        {job.employment_type && <span className="capitalize">{String(job.employment_type).replaceAll("_", " ")}</span>}
                        {job.remote_type && <span>· {({ onsite: "On-site", hybrid: "Hybrid", remote: "Remote" })[job.remote_type] || job.remote_type}</span>}
                    </div>
                    {(job.salary_min || job.salary_max) && (
                        <div className="mt-2 text-sm font-medium text-emerald-700">
                            {formatPHP(job.salary_min)}{job.salary_max ? ` – ${formatPHP(job.salary_max)}` : ""} / month
                        </div>
                    )}
                    {job.description && <p className="mt-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{job.description}</p>}
                    {!!(job.required_skills?.length) && (
                        <div className="mt-4">
                            <div className="text-xs font-semibold uppercase text-slate-500">Requirements</div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {job.required_skills.map((s) => <span key={s} className="text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-700">{s}</span>)}
                            </div>
                        </div>
                    )}
                    {!!(job.preferred_skills?.length) && (
                        <div className="mt-3">
                            <div className="text-xs font-semibold uppercase text-slate-500">Nice to have</div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {job.preferred_skills.map((s) => <span key={s} className="text-xs px-2 py-1 rounded-md bg-indigo-50 text-indigo-700">{s}</span>)}
                            </div>
                        </div>
                    )}
                    </div>
                </div>

                {/* Application form */}
                <form onSubmit={submit} className="bg-white rounded-2xl border shadow-sm p-6 md:p-8 mt-5">
                    <h2 className="text-lg font-semibold text-slate-900">Apply for this role</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Upload your resume and we’ll auto-match it against the requirements.</p>

                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <div><label className="text-xs text-slate-600">First name *</label><input className={field} value={form.first_name} onChange={set("first_name")} data-testid="apply-first" /></div>
                        <div><label className="text-xs text-slate-600">Last name *</label><input className={field} value={form.last_name} onChange={set("last_name")} data-testid="apply-last" /></div>
                        <div><label className="text-xs text-slate-600">Email *</label><input type="email" className={field} value={form.email} onChange={set("email")} data-testid="apply-email" /></div>
                        <div><label className="text-xs text-slate-600">Phone</label><input className={field} value={form.phone} onChange={set("phone")} data-testid="apply-phone" /></div>
                        <div><label className="text-xs text-slate-600">Current title</label><input className={field} value={form.current_title} onChange={set("current_title")} /></div>
                        <div><label className="text-xs text-slate-600">Current company</label><input className={field} value={form.current_company} onChange={set("current_company")} /></div>
                        <div><label className="text-xs text-slate-600">Location</label><input className={field} value={form.location} onChange={set("location")} /></div>
                        <div><label className="text-xs text-slate-600">LinkedIn / Portfolio</label><input className={field} value={form.linkedin} onChange={set("linkedin")} /></div>
                    </div>

                    <div className="mt-3">
                        <label className="text-xs text-slate-600">Resume (PDF, DOCX, or image)</label>
                        <label className="mt-1 flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-slate-300 text-sm text-slate-600 cursor-pointer hover:border-indigo-400">
                            <Upload className="size-4" />
                            <span className="truncate">{resumeName || "Choose file…"}</span>
                            <input type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                                onChange={(e) => { const f = e.target.files?.[0]; setResume(f || null); setResumeName(f?.name || ""); }}
                                data-testid="apply-resume" />
                        </label>
                    </div>

                    {err && <div className="mt-3 text-sm text-red-600" data-testid="apply-error">{err}</div>}

                    <button type="submit" disabled={submitting}
                        className="mt-5 w-full h-11 rounded-md bg-indigo-600 text-white font-medium text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        data-testid="apply-submit">
                        {submitting ? <><Loader2 className="size-4 animate-spin" /> Submitting…</> : "Submit application"}
                    </button>
                    <p className="mt-3 text-[11px] text-center text-slate-400">
                        Powered by SAHOD HRIS · Your information is shared only with the hiring company.
                    </p>
                </form>
            </div>
        </div>
    );
}
